import { getCatalogByKey, isFormLayoutEntry, matchCatalogByLabel } from "./circumference-utils";
import { SKINFOLD_SITES } from "./skinfold-utils";

export const DEFAULT_VISIBLE_MUSCLE_ROWS = 5;

const SKINFOLD_SITE_BY_KEY = SKINFOLD_SITES.reduce((acc, site) => {
  acc[site.key] = site;
  return acc;
}, {});

const toNumber = (value) => {
  if (value == null || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const readCircumferenceCm = (measurement, catalogKey) => {
  const catalog = getCatalogByKey(catalogKey);
  if (!catalog) {
    return null;
  }

  if (catalog.persistAs === "circumference" && catalog.apiField) {
    const value = measurement?.circumference?.[catalog.apiField];
    return value != null ? Number(value) : null;
  }

  if (catalog.persistAs === "custom") {
    const customEntry = (measurement?.custom_circumferences || []).find(
      (item) =>
        item?.label &&
        !isFormLayoutEntry(item) &&
        matchCatalogByLabel(item.label)?.key === catalogKey
    );
    if (customEntry?.value_cm != null) {
      return Number(customEntry.value_cm);
    }
  }

  return null;
};

const readSkinfoldMm = (measurement, siteKey) => {
  const catalog = SKINFOLD_SITE_BY_KEY[siteKey];
  if (!catalog) {
    return null;
  }

  const anthropometry = measurement?.anthropometry || {};
  if (anthropometry[catalog.apiField] != null && anthropometry[catalog.apiField] !== "") {
    return toNumber(anthropometry[catalog.apiField]);
  }

  const camelField = catalog.apiField.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
  if (anthropometry[camelField] != null && anthropometry[camelField] !== "") {
    return toNumber(anthropometry[camelField]);
  }

  const readings = measurement?.skinfold_readings || measurement?.skinfoldReadings || [];
  const reading = readings.find(
    (item) => String(item?.site || "").toLowerCase() === siteKey
  );
  if (reading?.mean_mm != null) {
    return toNumber(reading.mean_mm);
  }
  if (reading?.meanMm != null) {
    return toNumber(reading.meanMm);
  }

  return null;
};

const resolveArmCircumferenceCm = (measurement) => {
  const cmb = readCircumferenceCm(measurement, "cmb");
  if (cmb != null) {
    return cmb;
  }
  return readCircumferenceCm(measurement, "biceps");
};

export const MUSCLE_AREA_REGIONS = [
  {
    key: "arm",
    label: "Área muscular do braço",
    group: "Braço",
    metricType: "muscle_area",
    unit: "cm²",
    resolveValue: (measurement) => {
      const circumferenceCm = resolveArmCircumferenceCm(measurement);
      const skinfoldMm = readSkinfoldMm(measurement, "triceps");
      return calculateMuscleAreaCm2(circumferenceCm, skinfoldMm);
    },
  },
  {
    key: "thigh",
    label: "Área muscular da coxa",
    group: "Quadríceps",
    metricType: "muscle_area",
    unit: "cm²",
    resolveValue: (measurement) => {
      const circumferenceCm = readCircumferenceCm(measurement, "thigh");
      const skinfoldMm = readSkinfoldMm(measurement, "thigh");
      return calculateMuscleAreaCm2(circumferenceCm, skinfoldMm);
    },
  },
  {
    key: "calf",
    label: "Área muscular da panturrilha",
    group: "Panturrilha",
    metricType: "muscle_area",
    unit: "cm²",
    resolveValue: (measurement) => {
      const circumferenceCm = readCircumferenceCm(measurement, "calf");
      const skinfoldMm = readSkinfoldMm(measurement, "medial_calf");
      return calculateMuscleAreaCm2(circumferenceCm, skinfoldMm);
    },
  },
];

export const SKINFOLD_DETAIL_REGIONS = SKINFOLD_SITES.map((site) => ({
  key: `skinfold_${site.key}`,
  label: site.label,
  group: "Dobras",
  metricType: "skinfold",
  unit: "mm",
  resolveValue: (measurement) => readSkinfoldMm(measurement, site.key),
}));

export const MUSCLE_DETAIL_REGIONS = [
  {
    key: "circ_waist",
    label: "Cintura",
    group: "Circunferências",
    metricType: "circumference",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "waist"),
  },
  {
    key: "circ_hips",
    label: "Quadril",
    group: "Circunferências",
    metricType: "circumference",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "hips"),
  },
  {
    key: "circ_abdominal",
    label: "Abdômen",
    group: "Circunferências",
    metricType: "circumference",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "abdominal_circ"),
  },
  {
    key: "circ_neck",
    label: "Pescoço",
    group: "Circunferências",
    metricType: "circumference",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "neck"),
  },
  {
    key: "circ_biceps",
    label: "Bíceps",
    group: "Circunferências",
    metricType: "circumference",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "biceps"),
  },
  {
    key: "circ_cmb",
    label: "Circunferência muscular do braço",
    group: "Circunferências",
    metricType: "circumference",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "cmb"),
  },
  {
    key: "circ_calf",
    label: "Panturrilha",
    group: "Circunferências",
    metricType: "circumference",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "calf"),
  },
  {
    key: "circ_thigh",
    label: "Coxa",
    group: "Circunferências",
    metricType: "circumference",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "thigh"),
  },
  {
    key: "circ_wrist",
    label: "Punho",
    group: "Circunferências",
    metricType: "circumference",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "wrist"),
  },
  {
    key: "circ_chest",
    label: "Tórax",
    group: "Circunferências",
    metricType: "circumference",
    unit: "cm",
    resolveValue: (measurement) => readCircumferenceCm(measurement, "chest"),
  },
];

export const calculateMuscleAreaCm2 = (circumferenceCm, skinfoldMm) => {
  const circumference = toNumber(circumferenceCm);
  const skinfold = toNumber(skinfoldMm);
  if (circumference == null || skinfold == null) {
    return null;
  }

  const skinfoldCm = skinfold / 10;
  const adjusted = circumference - Math.PI * skinfoldCm;
  return (adjusted * adjusted) / (4 * Math.PI);
};

const sortMeasurementsChronologically = (measurements = []) =>
  [...measurements].sort((a, b) => {
    const dateA = a?.visit_date || "";
    const dateB = b?.visit_date || "";
    if (dateA !== dateB) {
      return dateA.localeCompare(dateB);
    }
    return Number(a?.id || 0) - Number(b?.id || 0);
  });

export const buildRegionTimeline = (measurements = [], region) => {
  if (!region?.resolveValue) {
    return [];
  }

  return sortMeasurementsChronologically(measurements)
    .map((measurement) => {
      const value = region.resolveValue(measurement);
      if (value == null || !Number.isFinite(value)) {
        return null;
      }
      return {
        measurementId: measurement.id,
        visitDate: measurement.visit_date,
        value,
      };
    })
    .filter(Boolean);
};

export const buildMuscleAreaTimeline = (measurements = [], region) => {
  const resolvedRegion =
    typeof region?.resolveValue === "function"
      ? region
      : MUSCLE_AREA_REGIONS.find((item) => item.key === region?.key || item.key === region);

  return buildRegionTimeline(measurements, resolvedRegion);
};

const formatMetricValue = (value, unit) => {
  if (value == null || !Number.isFinite(value)) {
    return "—";
  }
  return `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} ${unit}`;
};

const formatVariationValue = (delta, unit) => {
  if (delta == null || !Number.isFinite(delta)) {
    return "—";
  }
  const formatted = delta.toLocaleString("pt-BR", {
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
  });
  return `${formatted} ${unit}`;
};

const resolveTrend = (points) => {
  if (points.length < 2) {
    return "flat";
  }
  const last = points[points.length - 1].value;
  const previous = points[points.length - 2].value;
  if (last > previous) {
    return "up";
  }
  if (last < previous) {
    return "down";
  }
  return "flat";
};

const invertTrend = (trend) => {
  if (trend === "up") {
    return "down";
  }
  if (trend === "down") {
    return "up";
  }
  return "flat";
};

export const computeProgressPercent = (timeline = [], trend = "flat") => {
  if (timeline.length === 0) {
    return null;
  }
  if (timeline.length === 1) {
    return 60;
  }

  const values = timeline.map((point) => point.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const last = values[values.length - 1];

  if (max === min) {
    if (trend === "up") {
      return 85;
    }
    if (trend === "down") {
      return 45;
    }
    return 60;
  }

  return Math.round(((last - min) / (max - min)) * 100);
};

export const resolveProgressTone = (progressPercent, trend) => {
  if (trend === "down") {
    return "low";
  }
  if (progressPercent == null) {
    return "medium";
  }
  if (progressPercent >= 70) {
    return "high";
  }
  if (progressPercent >= 40) {
    return "medium";
  }
  return "low";
};

const buildDetailRowsFromRegions = (measurements = [], regions = []) =>
  regions
    .map((region) => {
      const timeline = buildRegionTimeline(measurements, region);
      if (timeline.length === 0) {
        return null;
      }

      const lastPoint = timeline[timeline.length - 1];
      const previousPoint = timeline.length > 1 ? timeline[timeline.length - 2] : null;
      const variation =
        previousPoint != null ? lastPoint.value - previousPoint.value : null;
      const rawTrend = resolveTrend(timeline);
      const trend =
        region.metricType === "skinfold" ? invertTrend(rawTrend) : rawTrend;
      const progressPercent = computeProgressPercent(timeline, rawTrend);
      const progressTone = resolveProgressTone(progressPercent, trend);

      return {
        key: region.key,
        regionLabel: region.label,
        groupLabel: region.group,
        metricType: region.metricType,
        unit: region.unit,
        lastValue: lastPoint.value,
        lastValueLabel: formatMetricValue(lastPoint.value, region.unit),
        variation,
        variationLabel: formatVariationValue(variation, region.unit),
        trend,
        progressPercent,
        progressLabel: progressPercent == null ? "—" : `${progressPercent}%`,
        progressTone,
        timeline,
      };
    })
    .filter(Boolean);

export const buildMuscleDetailRows = (measurements = []) =>
  buildDetailRowsFromRegions(measurements, MUSCLE_DETAIL_REGIONS);

export const buildSkinfoldDetailRows = (measurements = []) =>
  buildDetailRowsFromRegions(measurements, SKINFOLD_DETAIL_REGIONS);

export const getMuscleDetailFilterOptions = (rows = []) => {
  const optionsByLabel = new Map();

  rows.forEach((row) => {
    const label = row.regionLabel;
    if (!label) {
      return;
    }

    const existing = optionsByLabel.get(label);
    if (existing) {
      if (!existing.keys.includes(row.key)) {
        existing.keys.push(row.key);
      }
      return;
    }

    optionsByLabel.set(label, { label, keys: [row.key] });
  });

  return [...optionsByLabel.values()].sort((a, b) =>
    a.label.localeCompare(b.label, "pt-BR")
  );
};

export const filterMuscleDetailRows = (
  rows = [],
  { regions = [] } = {}
) => {
  if (regions.length === 0) {
    return rows;
  }

  const selectedRegions = new Set(regions);
  return rows.filter((row) => selectedRegions.has(row.regionLabel));
};

const getVariationSortValue = (row) => {
  if (row?.variation == null || !Number.isFinite(row.variation)) {
    return null;
  }
  return row.variation;
};

export const sortMuscleDetailRowsByVariation = (rows = [], direction = "desc") => {
  const multiplier = direction === "asc" ? 1 : -1;

  return [...rows].sort((firstRow, secondRow) => {
    const firstValue = getVariationSortValue(firstRow);
    const secondValue = getVariationSortValue(secondRow);

    if (firstValue == null && secondValue == null) {
      return firstRow.regionLabel.localeCompare(secondRow.regionLabel, "pt-BR");
    }
    if (firstValue == null) {
      return 1;
    }
    if (secondValue == null) {
      return -1;
    }
    if (firstValue === secondValue) {
      return firstRow.regionLabel.localeCompare(secondRow.regionLabel, "pt-BR");
    }

    return (firstValue - secondValue) * multiplier;
  });
};

const compareRowsByVariation = (firstRow, secondRow) => {
  if (firstRow.variation === secondRow.variation) {
    return firstRow.regionLabel.localeCompare(secondRow.regionLabel, "pt-BR");
  }
  return firstRow.variation - secondRow.variation;
};

export const getMuscleEvolutionHighlights = (rows = []) => {
  const comparableRows = rows.filter(
    (row) => row.variation != null && Number.isFinite(row.variation)
  );

  if (comparableRows.length === 0) {
    return null;
  }

  if (comparableRows.length === 1) {
    return {
      best: comparableRows[0],
      worst: comparableRows[0],
    };
  }

  const sortedRows = [...comparableRows].sort(compareRowsByVariation);

  return {
    best: sortedRows[sortedRows.length - 1],
    worst: sortedRows[0],
  };
};

export const exportMuscleDetailRowsCsv = (rows = []) => {
  const escapeCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const header = ["Área muscular", "Último valor", "Variação"];
  const lines = [
    header.join(","),
    ...rows.map((row) =>
      [row.regionLabel, row.lastValueLabel, row.variationLabel]
        .map(escapeCell)
        .join(",")
    ),
  ];

  return `\uFEFF${lines.join("\n")}`;
};
